/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import './Header.css';
import { LanguageSwitcher } from './LanguageSwitcher';
import Logo from './Logo/Logo';
import { HELP_CENTER_URL } from '../config/helpCenter';

interface HeaderProps {
  transparent?: boolean;
  showNavLinks?: boolean;
}

const Header: React.FC<HeaderProps> = ({ transparent = false, showNavLinks = true }) => {
  const { t } = useTranslation(['navigation', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Importer la police Lato
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Lato:wght@400;500;600;700;800;900&display=swap';
    document.head.appendChild(link);

    // Effet de scroll pour rendre le header opaque quand on défile
    const handleScroll = () => {
      if (window.scrollY > 80) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      document.head.removeChild(link);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileMenuOpen]);

  // Détermine si on est sur la landing page
  const isLandingPage = location.pathname === '/';

  // Quand on clique sur le logo, on redirige vers la landing page
  const handleLogoClick = () => {
    setMobileMenuOpen(false);
    navigate('/');
  };

  const handleLoginClick = () => {
    setMobileMenuOpen(false);
    navigate('/login');
  };

  const handleSignUpClick = () => {
    setMobileMenuOpen(false);
    navigate('/signup-user');
  };

  return (
    <header className={`rintintin-header ${scrolled || !transparent ? '' : 'header-transparent'} ${scrolled ? 'header-scrolled' : ''}`}>
      <div className="header-content">
        <button className="header-logo-button" onClick={handleLogoClick} aria-label="Retour à l'accueil Rintintin Pro">
          <Logo fontSize="1.8rem" className="header-logo-wordmark" />
        </button>

        {showNavLinks && (
          <div className="header-nav-links">
            <RouterLink to="/partenaires" className="header-nav-link">{t('header.ourPartners')}</RouterLink>
            <a href={isLandingPage ? "#tarifs" : "/#tarifs"} className="header-nav-link">{t('header.pricing')}</a>
            <a href={HELP_CENTER_URL} target="_blank" rel="noopener noreferrer" className="header-nav-link">{t('header.faq')}</a>
            <a href={isLandingPage ? "#contact" : "/#contact"} className="header-nav-link">{t('header.contact')}</a>
          </div>
        )}

        <div className="header-buttons">
          <div className="header-lang-switcher">
            <LanguageSwitcher />
          </div>

          {location.pathname !== '/login' && (
            <button className="header-login-button header-secondary-button" onClick={handleLoginClick}>
              {t('header.login')}
            </button>
          )}
          {isLandingPage && (
            <button className="header-signup-button header-primary-button" onClick={handleSignUpClick}>
              {t('header.startFree')}
            </button>
          )}
        </div>

        <button
          type="button"
          className="header-hamburger"
          onClick={() => setMobileMenuOpen((open) => !open)}
          aria-label={t('mobileHeader.menu', { ns: 'common' })}
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="header-mobile-menu">
          {location.pathname !== '/login' && (
            <button className="header-mobile-login" type="button" onClick={handleLoginClick}>
              {t('header.login')}
            </button>
          )}
          {isLandingPage && (
            <button className="header-mobile-signup" type="button" onClick={handleSignUpClick}>
              {t('header.startFree')}
            </button>
          )}
          {showNavLinks && (
            <nav className="header-mobile-nav" aria-label={t('mobileHeader.menu', { ns: 'common' })}>
              <RouterLink to="/partenaires" className="header-mobile-link" onClick={() => setMobileMenuOpen(false)}>
                {t('header.ourPartners')}
              </RouterLink>
              <a href={isLandingPage ? '#tarifs' : '/#tarifs'} className="header-mobile-link" onClick={() => setMobileMenuOpen(false)}>
                {t('header.pricing')}
              </a>
              <a
                href={HELP_CENTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="header-mobile-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('header.faq')}
              </a>
              <a href={isLandingPage ? '#contact' : '/#contact'} className="header-mobile-link" onClick={() => setMobileMenuOpen(false)}>
                {t('header.contact')}
              </a>
            </nav>
          )}
          <div className="header-mobile-lang">
            <LanguageSwitcher />
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
